import { useState } from 'react';
import classNames from 'classnames';
import Icon from 'components/icon/Icon';
import {
  useClick,
  useDismiss,
  useInteractions,
  useFloating,
  autoUpdate,
  flip,
  offset,
  FloatingPortal
} from '@floating-ui/react';
import './Tooltip.scss';

const TooltipPopper = props => {
  const {
    id,
    children,
    extraClassNames,
    icon,
    disabled,
    info,
    mediumIcon,
    graphic,
    video,
    videoMessage,
    label,
    labelObject,
    stylesLabel,
    styleRoot,
    inheritedStyles,
    onClick,
    openOnClick,
    iconStrokeColor,
    alert,
    iconFillColor,
    alertFill,
    tabIndex,
    inlineText,
    onMouseLeave,
    onBlur,
    onMouseEnter,
    onFocus,
    rightCenter = false,
    autoWidth = false,
    aboveModal = false
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [isActive, setIsActive] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    placement: rightCenter ? 'right' : 'bottom-end',
    strategy: 'fixed',
    open: isOpen,
    onOpenChange: setIsOpen,
    whileElementsMounted: autoUpdate,
    middleware: [offset(8), flip()]
  });
  const click = useClick(context);
  const dismiss = useDismiss(context, {
    outsidePressEvent: 'mousedown'
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    click,
    dismiss
  ]);

  const handleOnEvent = (e, event) => {
    if (event) {
      event();

      if (e._reactName === 'onClick') {
        setIsOpen(!isOpen);
        setIsActive(!isOpen);
      }

      if (e._reactName === 'onMouseEnter' || e._reactName === 'onFocus') {
        setIsOpen(true);
      }

      if (e._reactName === 'onMouseLeave' || e._reactName === 'onBlur') {
        setIsOpen(false);
      }
    }
  };

  return (
    <div
      className={classNames('c-tooltip c-tooltip--popper', extraClassNames, {
        'c-tooltip--alert': alert,
        'c-tooltip--info': info,
        'c-tooltip--video': video,
        'c-tooltip--label': label?.length > 0,
        'c-tooltip--inline-text': inlineText,
        'c-tooltip--inherited-styles': inheritedStyles,
        'c-tooltip--inherited-styles-active': inheritedStyles && isActive
      })}
      style={styleRoot}
    >
      <button
        className={classNames('c-button', {
          'c-button--tiny c-tooltip__trigger': !inheritedStyles,
          'c-button--disabled': props.disabled || props.pseudoDisabled // ok
        })}
        type={'button'}
        aria-labelledby={id?.length && !video ? id : `tooltip-label-${id}`}
        onClick={
          onClick && !openOnClick
            ? onClick
            : onClick && openOnClick
              ? e => handleOnEvent(e, onClick)
              : null
        }
        ref={refs.setReference}
        onMouseEnter={e => {
          onMouseEnter && handleOnEvent(e, onMouseEnter);
        }}
        onFocus={e => {
          onFocus && handleOnEvent(e, onFocus);
        }}
        onMouseLeave={e => {
          onMouseLeave && handleOnEvent(e, onMouseLeave);
        }}
        onBlur={e => {
          onBlur && handleOnEvent(e, onBlur);
        }}
        tabIndex={tabIndex}
        disabled={disabled}
        {...getReferenceProps()}
      >
        {icon && (
          <Icon
            id={icon}
            mediumIcon={mediumIcon}
            strokeColor={
              iconStrokeColor ? iconStrokeColor : alert ? 'color-error' : false
            }
            fillColor={
              iconFillColor ? iconFillColor : alertFill ? 'color-error' : false
            }
          />
        )}
        {label && (
          <span id={`tooltip-label-${id}`} style={stylesLabel}>
            {label}
          </span>
        )}
        {labelObject && labelObject}
      </button>

      <FloatingPortal>
        {isOpen && (
          <>
            {video && (
              <div
                className={classNames(
                  'c-tooltip__message c-tooltip-popper__message-video',
                  {
                    'c-tooltip-popper__message--hidden': !isOpen,
                    'c-tooltip-popper__message--auto-width': autoWidth
                  }
                )}
                role="tooltip"
                ref={refs.setFloating}
                style={floatingStyles}
                {...getFloatingProps()}
              >
                {children}
                {videoMessage && (
                  <section className="c-tooltip__info">{videoMessage}</section>
                )}
              </div>
            )}
            {!video &&
              (graphic ? (
                <div
                  id={id}
                  role="tooltip"
                  className={classNames(
                    'c-tooltip__message c-tooltip-popper__message',
                    {
                      'c-tooltip-popper__message--hidden': !isOpen,
                      'c-tooltip-popper__message-graphic': graphic,
                      'c-tooltip-popper__message--auto-width': autoWidth
                    }
                  )}
                  ref={refs.setFloating}
                  style={floatingStyles}
                  {...getFloatingProps()}
                >
                  {children}
                </div>
              ) : (
                <p
                  id={id}
                  role="tooltip"
                  className={classNames(
                    'c-tooltip__message c-tooltip-popper__message',
                    {
                      'c-tooltip-popper__message--hidden': !isOpen
                    }
                  )}
                  ref={refs.setFloating}
                  style={{ zIndex: aboveModal ? 103 : 1, ...floatingStyles }}
                  {...getFloatingProps()}
                >
                  <span>{children}</span>
                </p>
              ))}
          </>
        )}
      </FloatingPortal>
    </div>
  );
};

export default TooltipPopper;
